"use client";

/**
 * Purpose: About-page team member tile — portrait, name, role, and translated bio.
 * Inputs: `imageSrc` (Cloudinary public id), `name`, `role`, `bioKey` (RichText translation key),
 * `delay` (fadeUp stagger), optional `priority` (LCP hint) and `id` (override when several cards
 * render on one page).
 * Outputs: A motion.div card. The portrait's alt is derived from `aboutUs.teamPortraitAlt` +
 * `name` (translated in both locales) rather than a hard-coded string.
 */
import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "next-intl";
import RichText from "@components/text/RichText/RichText";
import { fadeUp } from "@lib/motion";
import './teamMemberCard.css'

interface TeamMemberCardProps {
  imageSrc: string;
  name: string;
  role: string;
  bioKey: string;
  delay: number;
  priority?: boolean;
  /** Override when more than one TeamMemberCard renders on the same page. */
  id?: string;
}

export function TeamMemberCard({ imageSrc, name, role, bioKey, delay, priority, id = 'team-member-card' }: TeamMemberCardProps) {
  const t = useTranslations('aboutUs')

  // The portrait is informative (it shows the person), so it gets a descriptive, translated alt
  // built from the member's name — docs/accessibility.md §3.
  const portraitAlt = t('teamPortraitAlt', { name })

  return (
    <motion.div
      id={id}
      className="team-member-card"
      {...fadeUp(delay)}
    >
      <div id={`${id}-avatar`} className="team-member-avatar">
        <Image
          src={imageSrc}
          alt={portraitAlt}
          width={144}
          height={144}
          sizes="144px"
          priority={priority}
          className="object-cover w-full h-full"
        />
      </div>
      {/* Name and role are plain paragraphs (not headings) so the page keeps its h2 structure */}
      <p id={`${id}-name`} className="question-bold">{name}</p>
      <p id={`${id}-role`} className="text-xs text-black/60 -mt-3">{role}</p>
      <p id={`${id}-bio`} className="text-sm text-black leading-relaxed">
        <RichText translationKey={bioKey} />
      </p>
    </motion.div>
  );
}
